import { del, get, post, postFileMedia, put } from '@webapps/utils';
import Amplify, { Auth } from 'aws-amplify';

import { COGNITO } from './config/aws-cognito';

Amplify.configure({
  aws_cognito_region: COGNITO.REGION,
  aws_user_pools_id: COGNITO.USER_POOL_ID,
  aws_user_pools_web_client_id: COGNITO.APP_CLIENT_ID,
});

const { NX_REST_API, NX_PROXY_API } = process.env;

export const getToken = {
  query: ({ password, username }) => Auth.signIn({ username, password }),
  format: (data) => data,
};

export const changePassword = {
  query: ({ newPassword, oldPassword }) =>
    Auth.currentAuthenticatedUser().then((user) =>
      Auth.changePassword(user, oldPassword, newPassword)
    ),
  format: (data) => data,
};

export const changeMail = {
  query: ({ email }) =>
    Auth.currentAuthenticatedUser().then((user) =>
      Auth.updateUserAttributes(user, { email })
    ),
  format: (data) => data,
};

export const confirmSignUp = {
  query: ({ code, username }) => Auth.confirmSignUp(username, code),
  format: (data) => data,
};

export const forgotPassword = {
  query: ({ username }) => Auth.forgotPassword(username),
  format: (data) => data,
};

export const confirmPasswordCode = {
  query: ({ newpassword, otp, username }) =>
    Auth.forgotPasswordSubmit(username, otp, newpassword),
  format: (data) => data,
};

export const confirmChangeEmailCode = {
  query: ({ code }) => Auth.verifyCurrentUserAttributeSubmit('email', code),
  format: (data) => data,
};

export const signOut = async () => {
  await Auth.signOut();
};

export const createStudent = {
  query: ({
    address,
    city,
    code,
    email,
    firstname,
    goal,
    lastname,
    password,
    phone,
    knowUs,
  }) =>
    post({
      url: `${NX_REST_API}/signup/individual`,
      data: {
        email,
        firstname,
        lastname,
        password,
        phone,
        address,
        code,
        city,
        goal,
        knowUs,
      },
    }),
};

export const createSchool = {
  query: ({
    address,
    city,
    code,
    email,
    name,
    siret,
    password,
    phone,
    subscription, // Free or Premium
    sub_expiration, // Date de fin d'abonnement
    gocardlessMandateId, // Gocardless Mandate id
    gocardlessCustomerId, // Gocardless customer id
    gocardlessSubscriptionId, // Gocardless Subscription id
    paymentType, // Moyen de paiement
    knowUs,
  }) =>
    post({
      // Local tests
      // url: `http://127.0.0.1:9090/signup/school`,
      url: `${NX_REST_API}/signup/school`,
      data: {
        email,
        phone,
        name,
        siret,
        password,
        address,
        code,
        city,
        subscription,
        sub_expiration,
        gocardlessMandateId,
        gocardlessCustomerId,
        gocardlessSubscriptionId,
        paymentType,
        knowUs,
      },
    }),
};

export const createBusiness = {
  query: ({
    address,
    city,
    code,
    email,
    name,
    siret,
    password,
    phone,
    subscription,
    sub_expiration, // Date de fin d'abonnement
    gocardlessMandateId,
    gocardlessCustomerId,
    gocardlessSubscriptionId,
    paymentType, // Moyen de paiement
    knowUs,
    opco,
    ogr,
  }) =>
    post({
      // Local tests
      // url: `http://127.0.0.1:9090/signup/business`,
      // PROD
      url: `${NX_REST_API}/signup/business`,
      data: {
        email,
        phone,
        name,
        siret,
        password,
        address,
        code,
        city,
        subscription,
        sub_expiration,
        gocardlessMandateId,
        gocardlessCustomerId,
        gocardlessSubscriptionId,
        paymentType,
        knowUs,
        opco,
        ogr
      },
    }),
};

export const upgradeBusiness = {
  query: ({
    accountId,
    name,
    subscription,
    sub_expiration, // Date de fin d'abonnement
    gocardlessMandateId,
    gocardlessCustomerId,
    gocardlessSubscriptionId,
  }) =>
    post({
      // Local tests
      // url: `http://127.0.0.1:9090/accounts/subscription/createSubscription`,
      // PROD
      url: `${NX_REST_API}/accounts/subscription/createSubscription`,
      data: {
        accountId,
        name,
        subscription,
        sub_expiration,
        gocardlessMandateId,
        gocardlessCustomerId,
        gocardlessSubscriptionId,
      },
    }),
};

export const stopSubscription = {
  query: ({ accountId, pennylaneSubscriptionId }) =>
    put({
      url: `${NX_REST_API}/accounts/subscription/stopSubscription`,
      data: { accountId, pennylaneSubscriptionId },
    }),
};

export const stopGiftSubscription = {
  query: ({ accountId }) =>
    put({
      url: `${NX_REST_API}/accounts/subscription/stopGiftSubscription/${accountId}`,
    }),
};

export const stopGoCardLessSubscription = {
  query: ({ subscriptionId }) =>
    post({
      url: `${NX_PROXY_API}/unsubscribeGoCardLess`,
      data: { subscriptionId },
    }),
};

export const reactivateSubscription = {
  query: ({ accountId, pennylaneSubscriptionId }) =>
    put({
      url: `${NX_REST_API}/accounts/subscription/reactivateSubscription`,
      data: { accountId, pennylaneSubscriptionId },
    }),
};

export const authenticateAccount = {
  query: ({ usernameOrEmail, password }) =>
    post({
      url: `${NX_REST_API}/accounts/authenticate`,
      data: { usernameOrEmail, password },
    }),
};

export const launchGoCardLess = {
  query: ({ sepaAmount, userEmail, userName, phone, key, type, siret }) =>
    post({
      url: `${NX_REST_API}/accounts/subscription/goCardLess`,
      data: { sepaAmount, userEmail, userName, phone, key, type, siret },
    }),
};

export const checkMandate = {
  query: ({ mandateId }) =>
    get({
      url: `${NX_PROXY_API}/mandate/${mandateId}`,
    }),
};

export const getUserId = {
  query: ({ username }) =>
    get({
      url: `${NX_REST_API}/profile/email/${username}`,
    }),
  format: (data) => data,
};

export const getProfile = {
  query: ({ email }) =>
    get({
      url: `${NX_REST_API}/profile/email/${email}`,
    }),
  format: (data) => data,
};

export const checkIfAccountExist = {
  query: ({ email, phone, siret }) =>
    get({
      url: `${NX_REST_API}/accounts/exist?email=${email}&phone=${phone}&siret=${siret}`,
    }),
  format: (data) => data,
};

export const getVitrineField = {
  query: ({ id }) =>
    get({
      url: `${NX_REST_API}/profile/${id}`,
    }),
  format: (data) => data,
};

export const getInfosDashboard = {
  query: ({ id }) =>
    get({
      url: `${NX_REST_API}/profile/dashboard/${id}`,
    }),
  format: ({ data }) => data,
};

export const changeMailOnBackend = {
  query: ({ email, id }) =>
    put({
      url: `${NX_REST_API}/accounts/${id}/update/email/${email}`,
    }),
};

export const changePhoneOnBackend = {
  query: ({ id, phone }) =>
    put({
      url: `${NX_REST_API}/accounts/${id}/update/phone/${phone}`,
    }),
};

export const changeNameOnBackend = {
  query: ({ id, firstname, lastname }) =>
    put({
      url: `${NX_REST_API}/accounts/${id}/name?firstname=${firstname}&lastname=${lastname}`,
    }),
};

export const getMedias = {
  query: ({ id, size, page }) =>
    get({
      url: `${NX_REST_API}/profile/medias/owner/${id}?size=${size}&page=${page}`,
    }),
  format: ({ data }) => data,
};

export const postMedia = {
  query: ({ data, id }) =>
    postFileMedia({
      url: `${NX_REST_API}/profile/medias/owner/${id}`,
      data,
    }),
};

export const putMedia = {
  query: ({ data, id }) =>
    put({
      url: `${NX_REST_API}/profile/medias/owner/${id}`,
      data,
    }),
};

export const viewProfileOnCVT = {
  query: ({ data, id }) =>
    put({
      url: `${NX_REST_API}/profile/views`,
      data,
    }),
};

export const deleteMedia = {
  query: ({ id }) =>
    del({
      url: `${NX_REST_API}/profile/medias/${id}`,
    }),
};
